import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faLinkedinIn } from '@fortawesome/free-brands-svg-icons';
import { faArrowUp, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import Logo from '~/assets/images/Logo.svg';
import Container from '~/components/ui/Container';

const defaultFooters = [
  {
    key: '1',
    title: 'Over Advieskeuze',
    data: [
      {
        text: 'Over Advieskeuze',
        link: '/home/overons',
      },
      {
        text: 'Contact',
        link: '/home/contact',
      },
      {
        text: 'Veelgestelde vragen',
        link: '/home/faq',
      },
      {
        text: 'Reviewbeleid',
        link: '/home/reviewbeleid',
      },
      {
        text: 'Cookiebeleid',
        link: '/home/cookiebeleid',
      },
      {
        text: 'Sitemap',
        link: '/home/sitemap',
      },
      {
        text: 'Nieuws voor partners',
        link: `${process.env.REACT_APP_EXTRANET_HOST}/nieuws`,
        target: '_blank',
      },
    ],
    isOpen: true,
  },
  {
    key: '2',
    title: 'Voor bedrijven',
    data: [
      {
        text: 'Inloggen',
        link: `${process.env.REACT_APP_EXTRANET_HOST}/account/logon`,
        target: '_blank',
      },
      {
        text: 'Aanmelden',
        link: `${process.env.REACT_APP_EXTRANET_HOST}/locaties/aanmelden`,
        target: '_blank',
      },
    ],
    isOpen: false,
  },
];

interface AccordionItem {
  key: string;
  title: string;
  data: Array<{
    text: string;
    link: string;
    target?: string;
  }>;
  isOpen: boolean;
}

interface FooterProps {
  accordions?: AccordionItem[];
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

// mobile view
const FooterAccordionItem: React.FC<
  AccordionItem & { toggleAccordion: () => void }
> = ({ title, data, isOpen, toggleAccordion }) => (
  <div className="my-0 lg:my-4">
    <div
      className="cursor-pointer border-t-[1px] border-stone-300 py-[8px] font-avantgarde text-[16px] font-bold leading-[22.4px] text-blue-900"
      role="button"
      tabIndex={0}
      onClick={toggleAccordion}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Space') {
          toggleAccordion();
        }
      }}
    >
      {title}
      <span
        className={`relative top-[5px] float-right inline-block h-3 w-3 transform border-r-[0.2em] border-t-[0.2em] border-blue-500 ${
          isOpen ? '-rotate-[45deg]' : 'rotate-[135deg]'
        } ml-2 mr-1 transition-transform duration-300`}
      ></span>
    </div>
    {isOpen && (
      <ul className="mt-2 text-sm font-normal">
        {data.map((item, index) => (
          <li key={item.text}>
            <a
              className={`mb-[12px] block no-underline ${index === 0 ? 'mt-[4px]' : 'mt-[0px]'}`}
              href={item.link}
              target={item.target}
            >
              <span className="font-roboto text-[12px] font-medium leading-[18px] text-blue-900">
                {item.text}
              </span>
            </a>
          </li>
        ))}
      </ul>
    )}
  </div>
);

// Web view
const FooterItem: React.FC<{
  title: string;
  data: Array<{
    text: string;
    link: string;
    target?: string;
  }>;
}> = ({ title, data }) => (
  <div className="mb-6 font-roboto">
    <h3 className="my-[8px] mb-[24px] font-avantgarde text-[20px] font-bold text-blue-900">
      {title}
    </h3>
    <ul className="mt-2 text-sm font-normal">
      {data.map((item, index) => (
        <li key={index}>
          <a
            className={`mb-[12px] flex no-underline ${index === 0 ? 'mt-[4px]' : 'mt-[0px]'} items-center font-body font-normal text-blue-900 hover:underline`}
            href={item.link}
            target={item.target}
          >
            <FontAwesomeIcon
              icon={faChevronRight as IconProp}
              fontSize={'14'}
              className="mr-2"
            />
            {item.text}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const Footer: React.FC<FooterProps> = () => {
  const [accordionState, setAccordionState] = useState(defaultFooters);

  const toggleAccordion = (accordionKey: string): void => {
    const updatedAccordions = accordionState.map((accord) => {
      if (accord.key === accordionKey) {
        return { ...accord, isOpen: !accord.isOpen };
      }
      return { ...accord, isOpen: false };
    });

    setAccordionState(updatedAccordions);
  };

  return (
    <footer
      role="contentinfo"
      className="relative bg-stone-50"
      itemScope
      itemType="http://schema.org/WPFooter"
    >
      <Container className="flex items-center pb-6 pt-12 md:pb-28 md:pt-20">
        <div
          className="mx-auto grid w-full grid-cols-1 gap-0 lg:grid-cols-12"
          itemProp="author"
          content="Advieskeuze.nl"
        >
          {/* Mobile: Company details */}
          <div className="col-span-3 col-start-1 mt-[30px] w-full lg:hidden">
            <a
              href="/"
              className="inline-block"
              aria-label="Ga terug naar home"
            >
              <img
                className="w-[10.923rem]"
                src={Logo as string}
                alt="Logo van Advieskeuze"
              />
            </a>
            <div className="mt-5 font-body text-[14px] font-normal text-blue-900">
              <div>Advieskeuze</div>
              <div>Dahliastraat 94 A</div>
              <div>4613 DP Bergen op Zoom</div>
            </div>
            <div className="mt-5">
              <div className="font-body text-[12px] font-bold leading-[18px] text-blue-900">
                Volg ons ook op
              </div>
              <div className="mb-4 mt-3 flex gap-[10px]">
                <a
                  href="https://www.linkedin.com/company/advieskeuze-nl/?originalSubdomain=nl"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Volg ons op LinkedIn"
                >
                  <FontAwesomeIcon
                    icon={faLinkedinIn as IconProp}
                    color={'#031C2D'}
                    fontSize={'24'}
                  />
                </a>
              </div>
            </div>
          </div>

          {/* Mobile: Accordion quick links */}
          {accordionState.map((accordion) => (
            <div
              className="col-span-3 lg:hidden"
              key={`${accordion.key}-outer`}
            >
              <FooterAccordionItem
                key={accordion.key}
                title={accordion.title}
                data={accordion.data}
                isOpen={accordion.isOpen}
                toggleAccordion={() => toggleAccordion(accordion.key)}
              />
            </div>
          ))}

          {/* Tablet and larger view */}
          {accordionState.map((accordion) => (
            <div
              className="col-span-3 hidden lg:block"
              key={`${accordion.key}-outer`}
            >
              <FooterItem
                key={accordion.key}
                title={accordion.title}
                data={accordion.data}
              />
            </div>
          ))}

          {/* Laptop: Company details */}
          <div
            className="col-span-3 hidden place-self-center font-roboto lg:col-span-6 lg:block"
            itemScope
            itemType="http://schema.org/PostalAddress"
          >
            <a
              href="/"
              className="inline-block"
              aria-label="Ga terug naar home"
            >
              <img
                loading="lazy"
                className="w-[10.923rem]"
                src={Logo as string}
                alt="Logo van Advieskeuze"
              />
            </a>
            <p className="mt-5 md:text-sm">
              <meta content="Primary" itemProp="contactType" />
              <span itemProp="name">Advieskeuze</span>
              <br />
              <span itemProp="streetAddress">Dahliastraat 94 A</span>
              <br />
              <span itemProp="postalCode">4613 DP</span>{' '}
              <span itemProp="addressLocality">Bergen op Zoom</span>
            </p>
            <div className="mt-5">
              <p className="text-[14px] font-bold leading-[21px] text-blue-900">
                Volg ons ook op
              </p>
              <div className="mt-3 flex items-center gap-[16px] p-1 md:gap-[8px]">
                <a
                  href="https://www.linkedin.com/company/advieskeuze-nl/?originalSubdomain=nl"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Volg ons op LinkedIn"
                >
                  <FontAwesomeIcon
                    icon={faLinkedinIn as IconProp}
                    fontSize={'24'}
                    color={'#031C2D'}
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </Container>

      <button
        aria-label="Terug naar boven"
        onClick={scrollToTop}
        className="absolute -top-8 right-10 z-10 flex hidden h-16 w-16 items-center justify-center rounded-full bg-blue-600 shadow-lg hover:bg-blue-800 sm:block"
      >
        <FontAwesomeIcon
          icon={faArrowUp as IconProp}
          className="pt-2 text-2xl text-white"
        />
      </button>

      {/* copyright */}
      <section className="relative w-full bg-blue-800 py-4 font-roboto text-sm text-white">
        <Container className="flex flex-col gap-4 md:flex-row md:items-center md:gap-10">
          <span className="w-full font-bold md:w-auto">
            Advieskeuze &copy; 2011&nbsp;-&nbsp;{new Date().getFullYear()}
          </span>
          <div className="flex flex-wrap items-center space-x-4">
            <a
              href="/home/privacy"
              className="text-white underline hover:text-white/75"
            >
              Privacybeleid
            </a>
            <a
              href="/home/disclaimer"
              className="text-white underline hover:text-white/75"
            >
              Disclaimer
            </a>
            <button
              className="text-white underline hover:text-white/75"
              type="button"
              onClick={() =>
                window.dispatchEvent(new Event('open-cookie-settings'))
              }
            >
              Cookie instellingen
            </button>
          </div>
        </Container>
      </section>
    </footer>
  );
};

export default Footer;
